import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, fmtDate, inr } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Search, ClipboardCheck, LayoutGrid, List as ListIcon } from "lucide-react";
import { toast } from "sonner";
import Documents from "@/components/app/Documents";
import RoofPhotoCapture from "@/components/app/RoofPhotoCapture";
import RowDeleteButton from "@/components/app/RowDeleteButton";
import SearchablePicker, { leadMatch, renderLeadRow } from "@/components/app/SearchablePicker";
import EmployeePicker from "@/components/app/EmployeePicker";

export default function Surveys() {
  const { state } = useLocation();
  const [surveys, setSurveys] = useState([]);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(Boolean(state?.leadId));
  const [q, setQ] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [pmFilter, setPmFilter] = useState("all");
  const [view, setView] = useState("grid");

  const load = () => {
    api.get("/surveys").then((r) => setSurveys(r.data));
    api.get("/leads/options?limit=500").then((r) => setLeads(r.data));
    api.get("/projects?lite=1").then((r) => setProjects(r.data));
  };
  useEffect(() => { load(); }, []);

  const assigneeOptions = useMemo(
    () => Array.from(new Set(surveys.map((s) => s.assignee_name).filter(Boolean))).sort(),
    [surveys]
  );
  const pmOptions = useMemo(
    () => Array.from(new Set(surveys.map((s) => s.project_manager_name).filter(Boolean))).sort(),
    [surveys]
  );

  const filtered = useMemo(() => {
    return surveys.filter((sv) => {
      if (assigneeFilter !== "all" && (sv.assignee_name || "") !== assigneeFilter) return false;
      if (pmFilter !== "all" && (sv.project_manager_name || "") !== pmFilter) return false;
      if (!q.trim()) return true;
      const lead = leads.find((l) => l.id === sv.lead_id);
      const s = q.toLowerCase();
      const hay = [
        lead?.name, lead?.phone, lead?.address,
        sv.site_address, sv.visit_notes, sv.roof_type, sv.net_meter_status,
        sv.surveyor, sv.assignee_name, sv.project_manager_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [surveys, leads, q, assigneeFilter, pmFilter]);

  return (
    <div data-testid="surveys-page">
      <PageHeader
        eyebrow="Engineering"
        title="Site Surveys"
        subtitle="Roof measurements, monthly consumption, GPS, photos, and electricity bill capture."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="new-survey-button">
                <Plus className="w-4 h-4 mr-2" /> New Survey
              </Button>
            </DialogTrigger>
            <SurveyDialog leadId={state?.leadId} projects={projects} onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />
      <div className="p-4 sm:p-8 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by customer, address, surveyor, PM, assignee…"
              className="rounded-none h-10 pl-9"
              data-testid="survey-search"
            />
          </div>
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="rounded-none h-10 w-full lg:w-56" data-testid="survey-assignee-filter">
              <SelectValue placeholder="All Assignees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              {assigneeOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={pmFilter} onValueChange={setPmFilter}>
            <SelectTrigger className="rounded-none h-10 w-full lg:w-56" data-testid="survey-pm-filter">
              <SelectValue placeholder="All Project Managers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Project Managers</SelectItem>
              {pmOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs value={view} onValueChange={setView} data-testid="survey-view-tabs">
          <TabsList className="rounded-none bg-[#F3F4F6] p-1 h-auto">
            <TabsTrigger value="grid" className="rounded-none text-xs uppercase tracking-widest font-bold" data-testid="view-grid-tab">
              <LayoutGrid className="w-3 h-3 mr-1.5" /> Grid View
            </TabsTrigger>
            <TabsTrigger value="list" className="rounded-none text-xs uppercase tracking-widest font-bold" data-testid="view-list-tab">
              <ListIcon className="w-3 h-3 mr-1.5" /> List View
            </TabsTrigger>
          </TabsList>

          <TabsContent value="grid" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((s) => (
                <SurveyCard key={s.id} survey={s} leads={leads} projects={projects} onUpdate={load} />
              ))}
              {filtered.length === 0 && <div className="col-span-full text-center text-sm text-[#9CA3AF] py-12">No surveys match your filters.</div>}
            </div>
          </TabsContent>

          <TabsContent value="list" className="mt-4">
            <SurveyListView surveys={filtered} leads={leads} projects={projects} onUpdate={load} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function SurveyListView({ surveys, leads, projects, onUpdate }) {
  const [editId, setEditId] = useState(null);
  if (surveys.length === 0) {
    return <div className="text-center text-sm text-[#9CA3AF] py-12">No surveys match your filters.</div>;
  }
  return (
    <>
      <div className="border border-[#E5E7EB] overflow-x-auto" data-testid="survey-list-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
            <tr>
              {["Visit", "Customer", "Roof", "Capacity", "Bill", "Assignee", "PM", "Status", ""].map((h) => (
                <th key={h} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {surveys.map((s) => {
              const lead = leads.find((l) => l.id === s.lead_id);
              return (
                <tr key={s.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`survey-list-row-${s.id}`}>
                  <td className="px-4 py-3 text-xs">{s.visit_date ? fmtDate(s.visit_date) : fmtDate(s.created_at)}</td>
                  <td className="px-4 py-3"><div className="font-semibold">{lead?.name || "—"}</div><div className="text-[10px] text-[#4B5563]">{lead?.phone || ""}</div></td>
                  <td className="px-4 py-3 text-xs">{s.roof_type} · {s.roof_length_ft}×{s.roof_width_ft} ft</td>
                  <td className="px-4 py-3 text-xs">{s.capacity_kw ? `${s.capacity_kw} kW` : "—"}</td>
                  <td className="px-4 py-3 text-xs">{s.monthly_bill_inr ? inr(s.monthly_bill_inr) : "—"}</td>
                  <td className="px-4 py-3 text-xs">{s.assignee_name || <span className="text-[#9CA3AF]">—</span>}</td>
                  <td className="px-4 py-3 text-xs">{s.project_manager_name || <span className="text-[#9CA3AF]">—</span>}</td>
                  <td className="px-4 py-3">
                    {s.visit_completed
                      ? <span className="text-[10px] uppercase tracking-widest font-bold bg-[#D1FAE5] text-[#065F46] px-1.5 py-0.5">Done</span>
                      : <span className="text-[10px] uppercase tracking-widest font-bold bg-[#FEF3C7] text-[#92400E] px-1.5 py-0.5">Pending</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setEditId(s.lead_id)}
                        className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline"
                        data-testid={`survey-list-edit-${s.id}`}
                      >
                        Edit →
                      </button>
                      <RowDeleteButton
                        endpoint={`/surveys/${s.id}`}
                        confirmMessage={`Delete this survey${lead ? ` for "${lead.name}"` : ""}? This cannot be undone.`}
                        onDeleted={onUpdate}
                        testid={`survey-list-delete-${s.id}`}
                        successMessage="Survey deleted"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editId && (
        <Dialog open={!!editId} onOpenChange={(v) => !v && setEditId(null)}>
          <SurveyDialog leadId={editId} projects={projects} onDone={() => { setEditId(null); onUpdate(); }} />
        </Dialog>
      )}
    </>
  );
}

function SurveyCard({ survey: s, leads, projects, onUpdate }) {
  const [editOpen, setEditOpen] = useState(false);
  const leadName = leads?.find((l) => l.id === s.lead_id)?.name;
  const completeVisit = async () => {
    try {
      await api.post(`/surveys/${s.id}/complete-visit`);
      toast.success("Site visit marked complete");
      onUpdate();
    } catch {
      toast.error("Failed to mark complete");
    }
  };
  return (
    <div className="flat-card p-5" data-testid={`survey-card-${s.id}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="label-eyebrow">{s.visit_date ? `Visit ${new Date(s.visit_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : `Logged ${fmtDate(s.created_at)}`}</div>
        <div className="flex items-center gap-3">
          {s.visit_completed ? (
            <span className="text-[10px] uppercase tracking-widest font-bold bg-[#D1FAE5] text-[#065F46] px-1.5 py-0.5">Visit Done</span>
          ) : (
            <button onClick={completeVisit} className="text-[10px] uppercase tracking-widest font-bold text-[#10B981] hover:underline" data-testid={`complete-visit-${s.id}`}>Mark Visit Done</button>
          )}
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogTrigger asChild>
              <button className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline" data-testid={`edit-visit-${s.id}`}>Edit</button>
            </DialogTrigger>
            {/* Mount only when open — otherwise every card fires the dialog's
                leads/options fetch on page load. */}
            {editOpen && <SurveyDialog leadId={s.lead_id} projects={projects} onDone={() => { setEditOpen(false); onUpdate(); }} />}
          </Dialog>
          <RowDeleteButton
            endpoint={`/surveys/${s.id}`}
            confirmMessage={`Delete this survey${leadName ? ` for "${leadName}"` : ""}? This cannot be undone.`}
            onDeleted={onUpdate}
            testid={`survey-card-delete-${s.id}`}
            successMessage="Survey deleted"
          />
        </div>
      </div>
      {leadName && <div className="text-base font-bold tracking-tight">{leadName}</div>}
      <div className="text-sm font-semibold tracking-tight mb-3">{s.roof_type} Roof · {s.roof_length_ft} × {s.roof_width_ft} ft</div>
      {s.visit_notes && <div className="text-xs text-[#0a0a0a] bg-[#F9FAFB] border-l-2 border-[#002FA7] px-2 py-2 mb-3 whitespace-pre-wrap">{s.visit_notes}</div>}
      <div className="grid grid-cols-2 gap-2 text-xs text-[#4B5563] border-t border-[#F3F4F6] pt-3">
        <div><span className="font-semibold text-[#0a0a0a]">{Number(s.monthly_consumption_kwh || 0).toLocaleString("en-IN")}</span> kWh/mo</div>
        <div><span className="font-semibold text-[#0a0a0a]">{inr(s.monthly_bill_inr || 0)}</span>/mo</div>
        <div>AC cable: <span className="font-semibold text-[#0a0a0a]">{s.ac_cable_length_m ? `${s.ac_cable_length_m} m` : "—"}</span></div>
        <div>DC cable: <span className="font-semibold text-[#0a0a0a]">{s.dc_cable_length_m ? `${s.dc_cable_length_m} m` : "—"}</span></div>
        <div>Net meter: <span className="font-semibold text-[#0a0a0a]">{s.net_meter_status}</span></div>
        <div>Surveyor: <span className="font-semibold text-[#0a0a0a]">{s.surveyor}</span></div>
        {s.assignee_name && <div>Assignee: <span className="font-semibold text-[#0a0a0a]">{s.assignee_name}</span></div>}
        {s.project_manager_name && <div>PM: <span className="font-semibold text-[#0a0a0a]">{s.project_manager_name}</span></div>}
      </div>
    </div>
  );
}

function SurveyDialog({ leadId, projects = [], onDone }) {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    lead_id: leadId || "", visit_date: "", visit_notes: "",
    roof_type: "RCC", roof_length_ft: 40, roof_width_ft: 30,
    monthly_consumption_kwh: "", monthly_bill_inr: "", transformer_kva: "",
    capacity_kw: "", panel_capacity_kw: "",
    net_meter_status: "Not Applied", shadow_notes: "", checklist: {},
    ac_cable_length_m: "", dc_cable_length_m: "",
    site_address: "", map_link: "",
    assignee_id: "", assignee_name: "",
    project_manager_id: "", project_manager_name: "",
  });
  // Selected lead object resolved by the picker (optionsEndpoint mode) —
  // drives the site-details autofill below.
  const [selectedLead, setSelectedLead] = useState(null);
  useEffect(() => {
    api.get("/users").then((r) => setEmployees((r.data || []).filter((u) => u.role !== "customer")));
    if (leadId) {
      api.get(`/surveys?lead_id=${leadId}`).then((r) => {
        if (r.data && r.data[0]) {
          const s = r.data[0];
          setForm((p) => ({
            ...p, ...s,
            ac_cable_length_m: s.ac_cable_length_m ?? "",
            dc_cable_length_m: s.dc_cable_length_m ?? "",
            transformer_kva: s.transformer_kva ?? "",
            capacity_kw: s.capacity_kw ?? "",
            panel_capacity_kw: s.panel_capacity_kw ?? "",
            monthly_consumption_kwh: s.monthly_consumption_kwh ?? "",
            monthly_bill_inr: s.monthly_bill_inr ?? "",
          }));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // Auto-populate site address, map link, transformer, capacity, consumption, bill from selected lead
  useEffect(() => {
    if (!form.lead_id || !selectedLead || selectedLead.id !== form.lead_id) return;
    const lead = selectedLead;
    setForm((p) => ({
      ...p,
      site_address: p.site_address || lead.address || "",
      map_link: p.map_link || lead.map_link || "",
      transformer_kva: (p.transformer_kva === "" || p.transformer_kva == null) ? (lead.transformer_kva ?? "") : p.transformer_kva,
      capacity_kw: (p.capacity_kw === "" || p.capacity_kw == null) ? (lead.capacity_kw ?? "") : p.capacity_kw,
      panel_capacity_kw: (p.panel_capacity_kw === "" || p.panel_capacity_kw == null) ? (lead.panel_capacity_kw ?? "") : p.panel_capacity_kw,
      monthly_consumption_kwh: (p.monthly_consumption_kwh === "" || p.monthly_consumption_kwh == null) ? (lead.monthly_consumption_kwh ?? "") : p.monthly_consumption_kwh,
      monthly_bill_inr: (p.monthly_bill_inr === "" || p.monthly_bill_inr == null) ? (lead.monthly_bill_inr ?? "") : p.monthly_bill_inr,
    }));
  }, [form.lead_id, selectedLead]);

  // Locate the project for this lead (if any) for the "Create Checklist" button
  const linkedProject = useMemo(
    () => projects.find((p) => p.lead_id === form.lead_id),
    [projects, form.lead_id]
  );

  const set = (k) => (v) => setForm({ ...form, [k]: v.target ? v.target.value : v });
  const setEmployee = (k_id, k_name) => (uid) => {
    const u = employees.find((e) => e.id === uid);
    setForm((p) => ({ ...p, [k_id]: uid, [k_name]: u?.name || "" }));
  };

  const submit = async () => {
    if (!form.lead_id) { toast.error("Pick a lead"); return; }
    const payload = {
      ...form,
      roof_length_ft: parseFloat(form.roof_length_ft) || 0,
      roof_width_ft: parseFloat(form.roof_width_ft) || 0,
      monthly_consumption_kwh: parseFloat(form.monthly_consumption_kwh) || 0,
      monthly_bill_inr: parseFloat(form.monthly_bill_inr) || 0,
      transformer_kva: form.transformer_kva === "" ? null : parseFloat(form.transformer_kva),
      capacity_kw: form.capacity_kw === "" ? null : parseFloat(form.capacity_kw),
      panel_capacity_kw: form.panel_capacity_kw === "" ? null : parseFloat(form.panel_capacity_kw),
      ac_cable_length_m: form.ac_cable_length_m === "" ? null : parseFloat(form.ac_cable_length_m),
      dc_cable_length_m: form.dc_cable_length_m === "" ? null : parseFloat(form.dc_cable_length_m),
      photos: [], electricity_bill: null,
    };
    await api.post("/surveys", payload);
    // Sync key fields back to lead so they propagate to projects + checklists
    const lead = selectedLead && selectedLead.id === form.lead_id ? selectedLead : null;
    const leadPatch = {};
    if (form.site_address && form.site_address !== (lead?.address || "")) leadPatch.address = form.site_address;
    if (form.map_link && form.map_link !== (lead?.map_link || "")) leadPatch.map_link = form.map_link;
    if (payload.transformer_kva && payload.transformer_kva !== (lead?.transformer_kva ?? null)) leadPatch.transformer_kva = payload.transformer_kva;
    if (payload.capacity_kw && payload.capacity_kw !== (lead?.capacity_kw ?? null)) leadPatch.capacity_kw = payload.capacity_kw;
    if (payload.panel_capacity_kw && payload.panel_capacity_kw !== (lead?.panel_capacity_kw ?? null)) leadPatch.panel_capacity_kw = payload.panel_capacity_kw;
    if (payload.monthly_consumption_kwh && payload.monthly_consumption_kwh !== (lead?.monthly_consumption_kwh ?? null)) leadPatch.monthly_consumption_kwh = payload.monthly_consumption_kwh;
    if (payload.monthly_bill_inr && payload.monthly_bill_inr !== (lead?.monthly_bill_inr ?? null)) leadPatch.monthly_bill_inr = payload.monthly_bill_inr;
    if (Object.keys(leadPatch).length > 0) {
      try { await api.patch(`/leads/${form.lead_id}`, leadPatch); } catch (_) { /* non-fatal */ }
    }
    toast.success("Survey saved");
    onDone();
  };

  const createChecklist = () => {
    if (!linkedProject) {
      toast.error("Create a project for this lead first");
      return;
    }
    onDone();
    navigate(`/delivery-checklists?new_for_project=${linkedProject.id}`);
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">Site Survey</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 pt-2">
        <div className="col-span-2 grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Assignee (Surveyor)</Label>
            <div className="mt-2">
              <EmployeePicker
                employees={employees}
                value={form.assignee_id}
                onChange={(id) => setEmployee("assignee_id", "assignee_name")(id)}
                placeholder="Select employee…"
                testid="survey-assignee-select"
              />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow">Project Manager</Label>
            <div className="mt-2">
              <EmployeePicker
                employees={employees}
                value={form.project_manager_id}
                onChange={(id) => setEmployee("project_manager_id", "project_manager_name")(id)}
                placeholder="Select PM…"
                testid="survey-pm-select"
              />
            </div>
          </div>
        </div>
        <div className="col-span-2">
          <Label className="label-eyebrow">Lead</Label>
          <SearchablePicker
            items={[]}
            value={form.lead_id}
            onChange={set("lead_id")}
            optionsEndpoint="/leads/options"
            onSelectItem={setSelectedLead}
            renderRow={renderLeadRow}
            selectedLabel={(l) => `${l.name || "(no name)"} — ${l.phone || "no phone"}`}
            placeholder="Search by name, phone or district…"
            testid="survey-lead-select"
            emptyHint="No leads match your search."
          />
          {form.lead_id && (
            <div className="mt-2 flex items-center justify-between bg-[#F9FAFB] border border-[#E5E7EB] px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]">
                {linkedProject ? (
                  <>Project: <span className="text-[#002FA7]">{linkedProject.qr_code} · {linkedProject.customer_name}</span></>
                ) : (
                  <>No project yet for this lead</>
                )}
              </div>
              <Button
                type="button"
                onClick={createChecklist}
                disabled={!linkedProject}
                size="sm"
                variant="outline"
                className="rounded-none border-[#002FA7] text-[#002FA7] disabled:opacity-50 text-[10px] uppercase tracking-widest font-bold h-8"
                data-testid="survey-create-checklist"
              >
                <ClipboardCheck className="w-3 h-3 mr-1" /> Create Delivery Checklist
              </Button>
            </div>
          )}
        </div>
        <div className="col-span-2">
          <Label className="label-eyebrow">Site Address (auto-filled from lead)</Label>
          <Textarea
            value={form.site_address}
            onChange={set("site_address")}
            className="rounded-none mt-2"
            rows={2}
            placeholder="Auto-populated from lead address"
            data-testid="survey-site-address"
          />
        </div>
        <div className="col-span-2">
          <Label className="label-eyebrow">Google Maps Link (auto-filled from lead)</Label>
          <div className="flex gap-2 mt-2">
            <Input
              value={form.map_link}
              onChange={set("map_link")}
              className="rounded-none h-10 flex-1"
              placeholder="https://maps.app.goo.gl/…"
              data-testid="survey-map-link"
            />
            {form.map_link && (
              <a
                href={form.map_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center h-10 px-3 border border-[#0a0a0a] uppercase tracking-widest text-xs font-bold text-[#002FA7]"
                data-testid="survey-map-link-open"
              >
                Open ↗
              </a>
            )}
          </div>
        </div>
        <Field label="Roof Type" value={form.roof_type} onChange={set("roof_type")} />
        <Field label="Net Meter Status" value={form.net_meter_status} onChange={set("net_meter_status")} />
        <Field label="Visit Date" type="date" value={form.visit_date} onChange={set("visit_date")} testid="survey-visit-date" />
        <div>
          <Label className="label-eyebrow">Visit Notes</Label>
          <Input value={form.visit_notes || ""} onChange={set("visit_notes")} className="rounded-none mt-2 h-10" placeholder="Gate code, time…" data-testid="survey-visit-notes" />
        </div>
        <Field label="Roof Length (ft)" type="number" value={form.roof_length_ft} onChange={set("roof_length_ft")} />
        <Field label="Roof Width (ft)" type="number" value={form.roof_width_ft} onChange={set("roof_width_ft")} />
        <Field label="Monthly Consumption (kWh)" type="number" value={form.monthly_consumption_kwh} onChange={set("monthly_consumption_kwh")} testid="survey-monthly-consumption" />
        <Field label="Monthly Bill (₹)" type="number" value={form.monthly_bill_inr} onChange={set("monthly_bill_inr")} testid="survey-monthly-bill" />
        <Field label="Transformer (kVA)" type="number" value={form.transformer_kva} onChange={set("transformer_kva")} testid="survey-transformer-kva" />
        <Field label="Inverter Capacity (kW)" type="number" value={form.capacity_kw} onChange={set("capacity_kw")} testid="survey-capacity-kw" />
        <Field label="Panel Capacity (kW)" type="number" value={form.panel_capacity_kw} onChange={set("panel_capacity_kw")} testid="survey-panel-capacity-kw" />
        <Field label="AC Cable Length (m)" type="number" value={form.ac_cable_length_m} onChange={set("ac_cable_length_m")} testid="survey-ac-cable" />
        <Field label="DC Cable Length (m)" type="number" value={form.dc_cable_length_m} onChange={set("dc_cable_length_m")} testid="survey-dc-cable" />
        <div className="col-span-2">
          <Label className="label-eyebrow">Shadow Analysis Notes</Label>
          <Textarea value={form.shadow_notes} onChange={set("shadow_notes")} className="rounded-none mt-2" rows={3} />
        </div>
        {form.lead_id && (
          <div className="col-span-2 border-t border-[#E5E7EB] pt-3">
            <RoofPhotoCapture leadId={form.lead_id} />
          </div>
        )}
        {form.lead_id && (
          <div className="col-span-2 border-t border-[#E5E7EB] pt-3">
            <Documents ownerType="lead" ownerId={form.lead_id} />
            <p className="text-[10px] text-[#4B5563] mt-2">Upload your survey checklist (PDF/photo) with doc type "Survey Checklist".</p>
          </div>
        )}
        <Button onClick={submit} className="col-span-2 rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="survey-submit">Save Survey</Button>
      </div>
    </DialogContent>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input type={type} value={value || ""} onChange={onChange} className="rounded-none mt-2 h-10" data-testid={testid} />
    </div>
  );
}
